/**
 * The relative period model (AG-298).
 *
 * Nothing here stores a concrete year. A control type says how its period is
 * anchored to the control date; a requirement says which years, relative to
 * that period's main year, its documents must cover. The concrete years are
 * resolved from the control's date at read time, so a control type keeps
 * working for the coming years without admin maintenance.
 */

export const PERIOD_MODES = ['CALENDAR_YEAR', 'ROLLING_MONTHS'] as const;
export type PeriodMode = (typeof PERIOD_MODES)[number];

export const COVERAGE_RULES = ['ALL_YEARS', 'ANY_YEAR'] as const;
export type CoverageRule = (typeof COVERAGE_RULES)[number];

/**
 * How far the main year may sit from the control's own year. `-1` is the year
 * before the control (the default), `0` the control's own year (arealkontroll).
 */
export const PERIOD_YEAR_OFFSET_MIN = -5;
export const PERIOD_YEAR_OFFSET_MAX = 0;

/** Bounds for one entry in `yearOffsets`; `0` is the main year itself. */
export const YEAR_OFFSET_MIN = -10;
export const YEAR_OFFSET_MAX = 0;

/** Bounds for `periodMonths` in rolling mode. */
export const PERIOD_MONTHS_MIN = 1;
export const PERIOD_MONTHS_MAX = 120;

/** The period configuration a control type carries. */
export interface ControlPeriodConfig {
  periodMode: PeriodMode;
  /** CALENDAR_YEAR only. Ignored in rolling mode. */
  periodYearOffset: number;
  /** ROLLING_MONTHS only: months back from the control date. Null otherwise. */
  periodMonths: number | null;
}

/** What every control type gets until an admin changes it: today's behaviour. */
export const DEFAULT_PERIOD_CONFIG: ControlPeriodConfig = {
  periodMode: 'CALENDAR_YEAR',
  periodYearOffset: -1,
  periodMonths: null,
};

/**
 * A control's period, resolved from its type's configuration and its date.
 * Dates are calendar days (`YYYY-MM-DD`), never instants.
 */
export interface ControlPeriod {
  mode: PeriodMode;
  startDate: string;
  endDate: string;
  /** CALENDAR_YEAR: year(control date) + offset. ROLLING_MONTHS: year(endDate). */
  mainYear: number;
  /** Every calendar year that intersects [startDate, endDate], ascending. */
  years: number[];
  /** The control date, or today when it is missing: what expiry dates are checked against. */
  referenceDate: string;
  /** True when the control has no date and today stood in for it. */
  isEstimated: boolean;
}
